import { useEffect, useState } from 'react';
import type { TenantConfig } from '@/theme/tenant';

/**
 * The white-label logo slot — forked from vCIP_droom_4.0/src/flow/components/BrandMark.tsx (owner
 * round 2026-08-28 item 4). A served https logo IS the mark inside a reserved band (zero layout
 * shift); a broken or absent logo falls back to the `--brand`-filled monogram. Wordmark-shaped
 * logos carry the name; symbol-shaped ones keep the name as text beside them.
 */
const LOGO_BAND_PX = { sm: 34, lg: 64 } as const;
const WORDMARK_MIN_ASPECT = 2;

export function BrandMark({
  tenant,
  size = 'sm',
  onNeedsName,
}: {
  tenant: TenantConfig;
  size?: 'sm' | 'lg';
  onNeedsName?: (needsName: boolean) => void;
}): JSX.Element {
  const { logoUrl, initial } = tenant.branding;
  const [brokenUrl, setBrokenUrl] = useState<string | null>(null);
  const [symbolUrl, setSymbolUrl] = useState<string | null>(null);
  const broken = logoUrl != null && brokenUrl === logoUrl;
  const showLogo = logoUrl != null && !broken;
  const isSymbol = showLogo && symbolUrl === logoUrl;
  const needsName = !showLogo || isSymbol;
  useEffect(() => {
    onNeedsName?.(needsName);
  }, [needsName, onNeedsName]);

  if (showLogo) {
    return (
      <span className={size === 'lg' ? 'brand-logo-box lg' : 'brand-logo-box'} style={{ height: LOGO_BAND_PX[size] }}>
        <img
          className={size === 'lg' ? 'brand-logo lg' : 'brand-logo'}
          src={logoUrl}
          alt={tenant.name}
          onError={() => setBrokenUrl(logoUrl)}
          onLoad={(e) => {
            const img = e.currentTarget;
            const symbol = img.naturalHeight > 0 && img.naturalWidth / img.naturalHeight < WORDMARK_MIN_ASPECT;
            setSymbolUrl(symbol ? logoUrl : null);
          }}
        />
      </span>
    );
  }
  return (
    <div className={size === 'lg' ? 'brand-mark lg' : 'brand-mark'} aria-hidden={true}>
      {(initial ?? tenant.name.charAt(0) ?? '·').toUpperCase()}
    </div>
  );
}
